import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminPermissions.css';

/**
 * Admin panel: user-centric role management.
 *
 * Lists Discord guild members (searchable), enriched with Steam link + ELO,
 * and lets admins assign/remove Discord roles per member.
 */
export default function AdminUsers({ perms, hideTitle = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const rolesById = useMemo(() => {
    const map = new Map();
    roles.forEach((r) => map.set(r.id, r));
    return map;
  }, [roles]);

  const fetchRoles = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(
        `${API_URL}/api/admin/permissions/${perms.guildId}/discord-roles`,
        { headers: getAuthHeaders() },
      );
      if (r.ok) setRoles(await r.json());
    } catch {
      // silent — modal will handle empty roles list
    }
  }, [perms.guildId, getAuthHeaders]);

  const fetchMembers = useCallback(
    async (query) => {
      if (!perms.guildId) return;
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (query) params.set('search', query);
        const r = await fetch(
          `${API_URL}/api/admin/permissions/${perms.guildId}/members?${params}`,
          { headers: getAuthHeaders() },
        );
        if (!r.ok) throw new Error(t('admin.loadMembersFailed'));
        const data = await r.json();
        setMembers(data.results || []);
      } catch (e) {
        setError(e.message || t('admin.errorGeneric'));
      } finally {
        setLoading(false);
      }
    },
    [perms.guildId, getAuthHeaders, t],
  );

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchMembers(search.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [search, fetchMembers]);

  const reloadMember = useCallback(
    async (discordId) => {
      try {
        const r = await fetch(
          `${API_URL}/api/admin/permissions/${perms.guildId}/members/${discordId}`,
          { headers: getAuthHeaders() },
        );
        if (!r.ok) return;
        const fresh = await r.json();
        setMembers((prev) =>
          prev.map((m) => (m.discord_id === discordId ? fresh : m)),
        );
        setEditing((prev) =>
          prev && prev.discord_id === discordId ? fresh : prev,
        );
      } catch {
        // silent
      }
    },
    [perms.guildId, getAuthHeaders],
  );

  const mutateRole = async (discordId, path, roleId) => {
    const res = await fetch(
      `${API_URL}/api/admin/permissions/${perms.guildId}/members/${discordId}/roles/${path}`,
      {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_id: roleId }),
      },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail?.error || d.error || t('admin.operationFailed'));
    }
  };

  const handleAddRole = (discordId, roleId) =>
    mutateRole(discordId, 'add', roleId).then(() => reloadMember(discordId));

  const handleRemoveRole = (discordId, roleId) =>
    mutateRole(discordId, 'remove', roleId).then(() => reloadMember(discordId));

  const sortedMembers = [...members].sort((a, b) =>
    (a.nick || a.global_name || a.username || '').localeCompare(
      b.nick || b.global_name || b.username || '',
      currentLocale(),
      { sensitivity: 'base' },
    ),
  );

  return (
    <div>
      {!hideTitle && <h1 className="admin-section-title">{t('admin.usersTitle')}</h1>}

      <div className="admin-search">
        <input
          type="text"
          placeholder={t('admin.searchDiscordUsername')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-info">{t('admin.loadingMembers')}</div>
      ) : members.length === 0 ? (
        <div className="admin-info">{t('admin.noMembersMatch')}</div>
      ) : (
        <div className="admin-user-list">
          {sortedMembers.map((m) => (
            <UserRow
              key={m.discord_id}
              member={m}
              rolesById={rolesById}
              onEdit={() => setEditing(m)}
            />
          ))}
        </div>
      )}

      {editing && (
        <UserRolesModal
          member={editing}
          allRoles={roles}
          onAddRole={handleAddRole}
          onRemoveRole={handleRemoveRole}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * Compact row for the user list with primary identity + role preview.
 */
function UserRow({ member, rolesById, onEdit }) {
  const { t } = useTranslation();
  const displayName = member.nick || member.global_name || member.username;
  const roleLabels = member.roles
    .map((id) => rolesById.get(id)?.name || id)
    .slice(0, 3);
  const extraCount = Math.max(0, member.roles.length - roleLabels.length);

  return (
    <div className="admin-user-row">
      <div className="admin-user-main">
        <div className="admin-user-name">{displayName}</div>
        <div className="admin-user-meta">
          @{member.username}
          {member.bot && <span className="admin-user-bot"> · BOT</span>}
          {member.steam && <span className="admin-user-steam"> · {t('admin.steamLinked')}</span>}
          {member.elo && (
            <span className="admin-user-elo"> · ELO {member.elo.elo}</span>
          )}
          {member.in_guild === false && (
            <span className="admin-user-left"> · {t('admin.notInDiscord')}</span>
          )}
        </div>
      </div>
      <div className="admin-user-roles">
        {roleLabels.map((name, idx) => (
          <span className="admin-perm-chip" key={idx}>
            {name}
          </span>
        ))}
        {extraCount > 0 && (
          <span className="admin-perm-chip admin-user-more">+{extraCount}</span>
        )}
      </div>
      <button
        className="admin-btn admin-btn-secondary"
        onClick={onEdit}
        disabled={member.in_guild === false}
        title={member.in_guild === false ? t('admin.notInDiscordTitle') : undefined}
      >
        {t('admin.manageRoles')}
      </button>
    </div>
  );
}

/**
 * Per-user modal showing current roles + add-role dropdown.
 */
function UserRolesModal({ member, allRoles, onAddRole, onRemoveRole, onClose }) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const displayName = member.nick || member.global_name || member.username;
  const rolesById = useMemo(() => {
    const map = new Map();
    allRoles.forEach((r) => map.set(r.id, r));
    return map;
  }, [allRoles]);

  const currentRoles = member.roles
    .map((id) => rolesById.get(id) || { id, name: id })
    .sort((a, b) => a.name.localeCompare(b.name, currentLocale()));

  const availableRoles = allRoles
    .filter((r) => !member.roles.includes(r.id) && !r.managed)
    .sort((a, b) => a.name.localeCompare(b.name, currentLocale()));

  const run = async (fn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(e.message || t('admin.errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal admin-perm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="admin-perm-modal-title">{displayName}</h3>
        <div className="admin-perm-modal-sub">
          @{member.username} · {member.discord_id}
        </div>

        {(member.steam || member.elo) && (
          <div className="admin-perm-modal-section">
            <div className="admin-perm-modal-heading">{t('admin.connection')}</div>
            {member.steam && (
              <div className="admin-user-detail">
                Steam:{' '}
                {member.steam.profile_url ? (
                  <a
                    href={member.steam.profile_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {member.steam.username}
                  </a>
                ) : (
                  member.steam.username
                )}{' '}
                ({member.steam.steamid64})
              </div>
            )}
            {member.elo && (
              <div className="admin-user-detail">
                ELO: {member.elo.elo} · {member.elo.matches_played} {t('admin.matchesWord')} ·{' '}
                {member.elo.wins}W / {member.elo.losses}L
              </div>
            )}
          </div>
        )}

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">{t('admin.currentRoles')}</div>
          {currentRoles.length === 0 && (
            <div className="admin-perm-empty">{t('admin.noRoles')}</div>
          )}
          <div className="admin-perm-modal-chips">
            {currentRoles.map((r) => (
              <span className="admin-perm-chip" key={r.id}>
                {r.name}
                <button
                  className="admin-perm-chip-remove"
                  onClick={() => run(() => onRemoveRole(member.discord_id, r.id))}
                  disabled={busy}
                  title={t('admin.remove')}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">{t('admin.addRole')}</div>
          {availableRoles.length === 0 ? (
            <div className="admin-perm-empty">{t('admin.allRolesSet')}</div>
          ) : (
            <div className="admin-perm-modal-row">
              <select
                className="admin-input admin-perm-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                disabled={busy}
              >
                <option value="">{t('admin.selectRole')}</option>
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button
                className="admin-btn"
                onClick={() =>
                  run(async () => {
                    await onAddRole(member.discord_id, selectedRole);
                    setSelectedRole('');
                  })
                }
                disabled={busy || !selectedRole}
              >
                {t('admin.add')}
              </button>
            </div>
          )}
        </div>

        {err && <div className="admin-error">{err}</div>}

        <div className="admin-confirm-actions">
          <button
            className="admin-btn admin-btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t('profile.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
